import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Stepper } from "@/components/ui/stepper";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { supabase as sharedSupabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLogoSettings } from '@/lib/settings/hooks/useLogoSettings';
import { useI18n } from '@/hooks/useI18n';
import { useClientAccountState } from "@/lib/shared-proposal/hooks/useClientAccountState";
import {
  useClientMailLanguage,
  MAIL_DEFAULT_LANGUAGE,
  type MailLanguage,
} from "@/lib/shared-proposal/hooks/useClientMailLanguage";
import { SendQuoteStepClient } from "./SendQuoteStepClient";
import { SendQuoteStep2Email } from "./SendQuoteStep2Email";
import { SendQuoteStep2 } from "./SendQuoteStep2";
import { QuoteReview } from "./components/QuoteReview";
import { toast } from "@/hooks/useToast";
import { sendQuote, updateItineraryStatus } from "@/services/itinerary.service";
import { PaymentOptionsForm } from "@/lib/payments/components/PaymentOptionsForm";
import { useProposalPaymentStep } from "@/lib/payments/hooks/useProposalPaymentStep";
import { paymentOptionsErrorKey } from "@/lib/payments/hooks/usePaymentOptions";
import { buildBankTransferEmailHtml } from "@/lib/payments/utils/bank-transfer";

interface SendQuoteEmailFlowProps {
  itinerary: DashboardItinerary;
  onBack: () => void;
  onComplete: (data: { pdfBase64?: string; pdfFilename?: string; emailSent: boolean }) => void;
  footerContainer?: HTMLElement | null;
}

export const SendQuoteEmailFlow = ({
  itinerary,
  onBack,
  onComplete,
  footerContainer,
}: SendQuoteEmailFlowProps) => {
  const { t, i18n } = useI18n();
  const { userData } = useAuth();

  // Metodi di pagamento che il cliente vedra': step obbligatorio, salvato prima dell'invio.
  const payment = useProposalPaymentStep(itinerary.id);

  // Stato dell'account del cliente: non basta sapere se l'email esiste, perche'
  // il cliente anagrafato alla creazione della travel request esiste gia' pur
  // non avendo mai ricevuto le credenziali.
  const { state: accountState, isLoading: isCheckingUser } = useClientAccountState(itinerary.contactEmail);

  // Lingua della mail: si parte dalla preferenza del cliente e resta
  // modificabile per questo solo invio (non tocca il profilo del cliente).
  const {
    profileLanguage: clientProfileLanguage,
    isLoading: isCheckingMailLanguage,
    lookupFailed: mailLanguageLookupFailed,
  } = useClientMailLanguage(itinerary.contactEmail);
  const [mailLanguage, setMailLanguage] = useState<MailLanguage | null>(null);

  useEffect(() => {
    // Solo la preselezione: se il TD ha gia' scelto, la lingua del profilo che
    // arriva dopo non gliela cambia sotto le mani.
    if (mailLanguage === null && clientProfileLanguage) setMailLanguage(clientProfileLanguage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientProfileLanguage]);

  // Finche' la lingua del profilo non e' arrivata (o se il cliente non ha un
  // profilo) vale il ripiego.
  const effectiveMailLanguage = mailLanguage ?? MAIL_DEFAULT_LANGUAGE;

  // Logo agenzia e preferenza salvata nel tab Logo
  const { settings: logoSettings } = useLogoSettings();
  const agencyLogoUrl = logoSettings?.agency?.logo_url ?? null;
  const agencyLogoSmallUrl = logoSettings?.agency?.logo_small_url ?? null;

  // Stepper state
  const [currentStep, setCurrentStep] = useState(1);

  // Modal state
  const [isCreating, setIsCreating] = useState(false);


  // Email customization states
  const [emailSubject, setEmailSubject] = useState(
    t('itinerary.emailSubjectPlaceholder', { title: itinerary.title })
  );
  const [customMessage, setCustomMessage] = useState('');
  const [logoChoice, setLogoChoice] = useState<'own' | 'agency' | null>(null);

  // Parte dal logo in uso (preferenza del profilo, con ripiego); resta modificabile per questo invio
  useEffect(() => {
    const source = logoSettings?.effective.source;
    if (logoChoice === null && (source === 'own' || source === 'agency')) {
      setLogoChoice(source);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoSettings]);

  // PDF states
  const [includePdf, setIncludePdf] = useState(true);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pdfBlobUrlRef = useRef<string | null>(null);

  // Solo un cliente che ha gia' fatto accesso riceve il link di solo accesso:
  // negli altri casi la proposta porta con se' le credenziali di primo accesso.
  const sharingMethod = accountState === 'active' ? 'existing_user' : 'create_user';

  // Le credenziali di primo accesso viaggiano solo nella mail: senza, il cliente
  // resta con un account di cui nessuno conosce la password (e a un invitato
  // viene sovrascritta quella che ha gia'). Si puo' saltare solo col cliente attivo.
  const isEmailRequired = sharingMethod === 'create_user';
  const [sendEmailChoice, setSendEmail] = useState(true);
  const sendEmail = isEmailRequired || sendEmailChoice;

  const resolveLogoUrls = (): { logoUrl: string | null; logoSmallUrl: string | null } => {
    if (logoChoice === 'own')    return { logoUrl: userData?.logo_url || null, logoSmallUrl: userData?.logo_small_url || null };
    if (logoChoice === 'agency') return { logoUrl: agencyLogoUrl, logoSmallUrl: agencyLogoSmallUrl };
    return { logoUrl: null, logoSmallUrl: null };
  };

  useEffect(() => {
    if (pdfBase64 !== null) {
      setPdfBase64(null);
      setPdfFilename(null);
      setPdfError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoChoice]);

  const generatePdf = async () => {
    setIsPdfGenerating(true);
    setPdfBase64(null);
    setPdfFilename(null);
    setPdfError(null);
    try {
      const { logoUrl, logoSmallUrl } = resolveLogoUrls();

      const payload: Record<string, string> = { itineraryId: itinerary.id };
      if (logoUrl) payload.logoUrl = logoUrl;
      if (logoSmallUrl) payload.logoSmallUrl = logoSmallUrl;
      const { data, error } = await sharedSupabase.functions.invoke('generate-pdf', {
        body: payload,
      });
      if (error || !data?.pdfBase64) {
        setPdfError(error?.message || 'Failed to generate PDF');
      } else {
        setPdfBase64(data.pdfBase64);
        setPdfFilename(data.fileName || `itinerary-${itinerary.id}.pdf`);
      }
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePreviewPdf = () => {
    if (!pdfBase64) return;
    if (pdfBlobUrlRef.current) {
      URL.revokeObjectURL(pdfBlobUrlRef.current);
    }
    const byteChars = atob(pdfBase64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    pdfBlobUrlRef.current = url;
    window.open(url, '_blank');
  };

  const handleSendQuote = async () => {
    if (sendEmail && includePdf && (!pdfBase64 || !pdfFilename)) {
      return;
    }

    setIsCreating(true);

    try {
      try {
        await payment.save();
      } catch (error) {
        toast({ title: t(paymentOptionsErrorKey(error)), variant: "destructive" });
        return;
      }

      const result = await sendQuote({
        draftId: itinerary.id,
        pdfBase64: sendEmail && includePdf ? pdfBase64 || '' : undefined,
        pdfFilename: sendEmail && includePdf ? pdfFilename || '' : undefined,
        to: itinerary.contactEmail,
        subject: emailSubject,
        shouldSendEmail: sendEmail,
        sharingMethod,
        language: effectiveMailLanguage,
        variables: {
          client_first_name: itinerary.travelRequest?.contact?.firstName || '',
          client_last_name: itinerary.travelRequest?.contact?.lastName || '',
          itinerary_title: itinerary.title,
          start_date: itinerary.startDate,
          end_date: itinerary.endDate,
          custom_message: customMessage,
          payment: getPaymentData(),
          logo_url: resolveLogoUrls().logoUrl || '',
          cancellation_policy_notes: itinerary.cancellationPolicyNotes || '',
        },
      });

      if (result.success) {
        await updateItineraryStatus(itinerary.id, 'sended');

        if (sendEmail) {
          toast({ variant: 'success',
            title: t('itinerary.quoteSent'),
            description: t('itinerary.quoteSentDesc'),
          });
        } else {
          toast({ variant: 'success',
            title: t('itinerary.quotePrepared'),
            description: t('itinerary.quotePreparedDescription'),
          });
        }

        onComplete({
          pdfBase64: pdfBase64 || undefined,
          pdfFilename: pdfFilename || undefined,
          emailSent: sendEmail,
        });
      } else {
        if (result.errorCode === 'agency_stripe_not_configured') {
          toast({
            title: t('itinerary.paymentNotConfiguredTitle'),
            description: result.message || t('itinerary.paymentNotConfiguredDescription'),
            variant: "destructive",
          });
        } else {
          toast({
            title: t("common.error"),
            description: t("itinerary.sendQuoteError"),
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Error sending quote:', error);
      toast({
        title: t("common.error"),
        description: t("itinerary.sendQuoteError"),
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Coordinate del bonifico nella mail, con le etichette nella lingua della mail.
  // Vuota se il bonifico non e' fra i metodi scelti.
  const getPaymentData = () => {
    if (!payment.form?.methods.includes('bank_transfer')) return '';
    return buildBankTransferEmailHtml(payment.form, i18n.getFixedT(effectiveMailLanguage));
  };

  // 5 step: dati del cliente, pagamento, email, PDF & logo, riepilogo.
  // Senza mail il PDF non ha dove essere allegato: lo step si salta.
  const STEP_CLIENT = 1;
  const STEP_PAYMENT = 2;
  const STEP_EMAIL = 3;
  const STEP_PDF = 4;
  const STEP_REVIEW = 5;
  const steps = [
    { label: t('itinerary.stepClientData') },
    { label: t('paymentOptions.review.title') },
    { label: t('itinerary.stepEmailConfiguration') },
    { label: t('itinerary.stepLogoAndPdf') },
    { label: t('itinerary.stepReview') }
  ];

  const skippedSteps = !sendEmail ? [STEP_PDF] : [];

  const handleNext = () => {
    if (currentStep === STEP_PAYMENT && !payment.validate()) return;
    if (currentStep === STEP_EMAIL && !sendEmail) {
      setCurrentStep(STEP_REVIEW);
    } else if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep === STEP_REVIEW && !sendEmail) {
      setCurrentStep(STEP_EMAIL);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };

  const isPdfReady = !isPdfGenerating && pdfBase64 !== null;
  const hasLogoSelected = logoChoice !== null;

  const canProceedFromPdfStep = includePdf ? (hasLogoSelected && isPdfReady) : true;
  const canSend = (() => {
    if (sendEmail) {
      const pdfCondition = includePdf ? (hasLogoSelected && isPdfReady) : true;
      return pdfCondition;
    } else {
      return true;
    }
  })();

  return (
    <>
      <div className="space-y-6 px-6 py-6">
        <Stepper steps={steps} currentStep={currentStep} skippedSteps={skippedSteps} />

        <div className="min-h-[400px]">
        {currentStep === STEP_CLIENT && (
          <SendQuoteStepClient
            itinerary={itinerary}
            accountState={accountState}
            isCheckingUser={isCheckingUser}
            mailLanguage={effectiveMailLanguage}
            onMailLanguageChange={setMailLanguage}
            clientProfileLanguage={clientProfileLanguage}
            isCheckingMailLanguage={isCheckingMailLanguage}
            mailLanguageLookupFailed={mailLanguageLookupFailed}
          />
        )}

        {currentStep === STEP_PAYMENT && (
          payment.form ? (
            <PaymentOptionsForm
              value={payment.form}
              onChange={payment.onChange}
              errors={payment.errors}
              stripeActive={payment.stripeActive}
              defaults={payment.defaults}
            />
          ) : (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          )
        )}

        {currentStep === STEP_EMAIL && (
          <SendQuoteStep2Email
            sendEmail={sendEmail}
            isEmailRequired={isEmailRequired}
            onSendEmailChange={setSendEmail}
            emailSubject={emailSubject}
            customMessage={customMessage}
            onEmailSubjectChange={setEmailSubject}
            onCustomMessageChange={setCustomMessage}
            subjectPlaceholder={t('itinerary.emailSubjectPlaceholder', { title: itinerary.title })}
          />
        )}

        {currentStep === STEP_PDF && (
          <SendQuoteStep2
            logoChoice={logoChoice}
            onLogoChoiceChange={setLogoChoice}
            hasOwnLogo={!!userData?.logo_url}
            hasAgencyLogo={!!agencyLogoUrl}
            ownLogoUrl={userData?.logo_url}
            ownLogoSmallUrl={userData?.logo_small_url}
            agencyLogoUrl={agencyLogoUrl}
            agencyLogoSmallUrl={agencyLogoSmallUrl}
            isPdfGenerating={isPdfGenerating}
            pdfBase64={pdfBase64}
            pdfFilename={pdfFilename}
            pdfError={pdfError}
            onGeneratePdf={generatePdf}
            onPreviewPdf={handlePreviewPdf}
            includePdf={includePdf}
            onIncludePdfChange={setIncludePdf}
          />
        )}

        {currentStep === STEP_REVIEW && (
          <QuoteReview
            itinerary={itinerary}
            sendEmail={sendEmail}
            emailSubject={emailSubject}
            customMessage={customMessage}
            includePdf={includePdf}
            logoChoice={logoChoice}
            pdfFilename={pdfFilename}
            sharingMethod={sharingMethod}
            contactEmail={itinerary.contactEmail}
            mailLanguage={effectiveMailLanguage}
            clientProfileLanguage={clientProfileLanguage}
            paymentMethods={payment.form?.methods ?? []}
          />
        )}
        </div>
      </div>

      {/* Navigation buttons — portaled into the Modal's fixed footer (outside the scroll area) */}
      {footerContainer && createPortal(
        <>
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={isCreating}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {currentStep === 1 ? t('common.back') : t('common.previous')}
          </Button>

          {currentStep < STEP_REVIEW && (
            <Button
              onClick={handleNext}
              disabled={
                (currentStep === STEP_PDF && !canProceedFromPdfStep) ||
                (currentStep === STEP_PAYMENT && payment.isLoading)
              }
            >
              {t('common.next')}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}

          {currentStep === STEP_REVIEW && (
            <Button
              onClick={handleSendQuote}
              disabled={!canSend || isCreating || payment.isSaving}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common.sending')}
                </>
              ) : (
                t(sendEmail ? 'itinerary.sendQuote' : 'itinerary.prepareQuote')
              )}
            </Button>
          )}
        </>,
        footerContainer
      )}
    </>
  );
};
